import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { 
  Inbox, List, Calendar, Bot, TrendingUp, FileText,
  ArrowRight, ChevronRight, Sparkles, Zap, CheckCircle
} from 'lucide-react';

const WELCOME_MODAL_KEY = 'speedforce_welcome_shown';

const features = [
  {
    icon: Inbox,
    title: 'AI Intake',
    description: 'Capture ideas naturally. Our AI structures them into proper requirements with problem statements, success criteria, and user personas.',
    color: 'bg-blue-500'
  },
  {
    icon: List,
    title: 'Smart Backlog',
    description: 'View your backlog with hierarchy (Epics → Features → Stories), RICE scores, and quality indicators. Filter, search, and organize effortlessly.',
    color: 'bg-green-500'
  },
  {
    icon: Calendar,
    title: 'Sprint Planning',
    description: 'Drag-and-drop sprint board with AI-powered health predictions. Know if your sprint is on track before it\'s too late.',
    color: 'bg-purple-500'
  },
  {
    icon: Bot,
    title: 'AI Agents',
    description: '10 specialized AI agents work together: refining items, scoring priorities, detecting duplicates, generating standups, and more.',
    color: 'bg-pink-500'
  },
  {
    icon: FileText,
    title: 'One-Pagers',
    description: 'Upload existing docs or let AI generate product one-pagers with vision, FAQs, roadmap, and success metrics.',
    color: 'bg-amber-500'
  },
  {
    icon: TrendingUp,
    title: 'ADO Sync',
    description: 'Bi-directional sync with Azure DevOps. Import work items, sync updates, and keep everything in harmony.',
    color: 'bg-cyan-500'
  }
];

const workflow = [
  { step: '1', title: 'Capture', description: 'New ideas via AI Intake', icon: Inbox },
  { step: '2', title: 'Refine', description: 'AI improves quality', icon: Sparkles },
  { step: '3', title: 'Prioritize', description: 'RICE scoring', icon: TrendingUp },
  { step: '4', title: 'Plan', description: 'Sprint assignment', icon: Calendar },
  { step: '5', title: 'Execute', description: 'Track & report', icon: Zap }
];

export default function WelcomeModal({ forceShow = false, onClose }) {
  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    // Check if we should show the modal
    if (forceShow) {
      setOpen(true);
      return;
    }
    
    const hasSeenWelcome = localStorage.getItem(WELCOME_MODAL_KEY);
    if (!hasSeenWelcome) {
      setOpen(true);
    }
  }, [forceShow]);

  const handleClose = () => {
    localStorage.setItem(WELCOME_MODAL_KEY, 'true');
    setOpen(false);
    onClose?.();
  };

  const handleNext = () => {
    if (currentPage < 2) {
      setCurrentPage(currentPage + 1);
    } else {
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] p-0 rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-[#1a1a1a] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#98139C] to-[#4F0077] p-6 text-white shrink-0">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Zap size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Welcome to SPEEDFORCE</h1>
              <p className="text-white/80 text-sm">Your AI-powered PM Copilot</p>
            </div>
          </div>
        </div>

        {/* Page Indicator */}
        <div className="flex justify-center gap-2 py-4 border-b border-[#e3e3e3] dark:border-white/10 shrink-0">
          {[0, 1, 2].map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                currentPage === page 
                  ? 'bg-[#98139C]' 
                  : 'bg-[#e3e3e3] dark:bg-white/20 hover:bg-[#98139C]/50'
              }`}
            />
          ))}
        </div>

        {/* Content - scrollable */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {/* Page 1: Overview */}
          {currentPage === 0 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-[#30302F] dark:text-white mb-2">
                  What can SPEEDFORCE do?
                </h2>
                <p className="text-[#30302F]/80 dark:text-white/80">
                  From idea to delivery, AI assists every step of your product management workflow.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {features.map((feature, idx) => (
                  <div 
                    key={idx}
                    className="p-4 border border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C] transition-colors"
                  >
                    <div className={`w-10 h-10 ${feature.color} rounded-lg flex items-center justify-center mb-3`}>
                      <feature.icon size={20} className="text-white" />
                    </div>
                    <h3 className="font-bold text-[#30302F] dark:text-white text-sm mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-xs text-[#30302F]/80 dark:text-white/80 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Page 2: Workflow */}
          {currentPage === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-[#30302F] dark:text-white mb-2">
                  The PM Workflow
                </h2>
                <p className="text-[#30302F]/80 dark:text-white/80">
                  A streamlined flow from capture to execution, powered by AI at every step.
                </p>
              </div>

              <div className="flex items-center justify-between px-4">
                {workflow.map((item, idx) => (
                  <div key={idx} className="flex items-center">
                    <div className="text-center">
                      <div className="w-14 h-14 bg-[#f0dcf0] dark:bg-[#98139C]/20 rounded-full flex items-center justify-center mb-2 mx-auto">
                        <item.icon size={24} className="text-[#98139C]" />
                      </div>
                      <div className="text-xs font-bold text-[#98139C]">{item.step}</div>
                      <div className="text-sm font-medium text-[#30302F] dark:text-white">{item.title}</div>
                      <div className="text-[11px] text-[#30302F]/70 dark:text-white/70">{item.description}</div>
                    </div>
                    {idx < workflow.length - 1 && (
                      <ChevronRight size={20} className="text-[#98139C]/50 mx-2" />
                    )}
                  </div>
                ))}
              </div>

              <div className="bg-[#f9f5fa] dark:bg-[#98139C]/10 p-4 rounded-lg mt-6">
                <h4 className="font-bold text-[#30302F] dark:text-white text-sm mb-2 flex items-center gap-2">
                  <Bot size={16} className="text-[#98139C]" />
                  AI Agents Work Behind the Scenes
                </h4>
                <p className="text-xs text-[#30302F]/80 dark:text-white/80">
                  Our 10 specialized agents automatically refine your items, detect duplicates, 
                  calculate RICE scores, predict sprint health, and generate reports. 
                  You stay in control with human-in-the-loop approvals.
                </p>
              </div>
            </div>
          )}

          {/* Page 3: Getting Started */}
          {currentPage === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-[#30302F] dark:text-white mb-2">
                  Quick Start Guide
                </h2>
                <p className="text-[#30302F]/80 dark:text-white/80">
                  Three ways to get started with SPEEDFORCE
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 border border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C] transition-colors">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="font-bold text-[#30302F] dark:text-white mb-1">
                      Import from Azure DevOps
                    </h3>
                    <p className="text-sm text-[#30302F]/80 dark:text-white/80">
                      Already have work items in ADO? Go to <strong>Settings → Azure DevOps</strong> to connect 
                      and import your Epics, Features, and Stories instantly.
                    </p>
                  </div>
                  <CheckCircle size={20} className="text-green-500 shrink-0" />
                </div>

                <div className="flex items-start gap-4 p-4 border border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C] transition-colors">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="font-bold text-[#30302F] dark:text-white mb-1">
                      Start Fresh with AI Intake
                    </h3>
                    <p className="text-sm text-[#30302F]/80 dark:text-white/80">
                      Have a new idea? Click <strong>New Intake</strong> and describe it in plain language. 
                      AI will structure it into a proper requirement.
                    </p>
                  </div>
                  <CheckCircle size={20} className="text-green-500 shrink-0" />
                </div>

                <div className="flex items-start gap-4 p-4 border border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C] transition-colors">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="font-bold text-[#30302F] dark:text-white mb-1">
                      Upload Your One-Pager
                    </h3>
                    <p className="text-sm text-[#30302F]/80 dark:text-white/80">
                      Have an existing product document? Go to a <strong>Product</strong> page and upload 
                      your .docx/.pdf. AI extracts vision, scope, and FAQs automatically.
                    </p>
                  </div>
                  <CheckCircle size={20} className="text-green-500 shrink-0" />
                </div>
              </div>

              <div className="bg-gradient-to-r from-[#98139C]/10 to-[#4F0077]/10 p-4 rounded-lg text-center">
                <p className="text-sm text-[#30302F] dark:text-white">
                  Need help? Click <strong>AI Agents</strong> in the sidebar to see all available agents 
                  and their capabilities.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#e3e3e3] dark:border-white/10 flex items-center justify-between shrink-0">
          <button
            onClick={handleClose}
            className="text-sm text-[#30302F]/80 dark:text-white/80 hover:text-[#30302F] dark:hover:text-white"
          >
            Skip tour
          </button>
          <button
            onClick={handleNext}
            className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-6 h-10 flex items-center gap-2 transition-colors"
          >
            {currentPage === 2 ? 'Get Started' : 'Next'}
            <ArrowRight size={16} />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
